import { useEffect, useState } from "react";
import { apiClient, ApiError, NotAuthenticatedError } from "./client.js";
import { useSession } from "../auth/use-session.js";
import type { MeResponse } from "./types.js";

// 1.D.8 useMe-hook. /me-endpointin lataus session-tilan mukaan.
//
// Better-Auth:n useSession kertoo isLoggedIn:n cookie-pohjaisesti, mutta
// rooli- ja projekti-listat tulevat vain meidan API:n /me-vastauksesta.
// useMe lataa /me kun ollaan kirjautuneita ja paivittaa session-muutoksissa.
//
// V1: ei caching-kerrosta — jokainen useMe-instanssi tekee oman fetchin.
// Selaa- + Nav-komponentit kayttavat tata; v2:ssa otetaan kayttoon
// suspended/cached query.

export type UseMeResult = {
  me: MeResponse | null;
  isLoading: boolean;
  error: ApiError | null;
};

export function useMe(): UseMeResult {
  const session = useSession();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!session.isLoggedIn) {
      setMe(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .getMe()
      .then((res) => {
        if (cancelled) return;
        setMe(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) {
          setMe(null);
        } else if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(new ApiError(err instanceof Error ? err.message : String(err), 0));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session.isLoggedIn, session.user?.id]);

  return { me, isLoading, error };
}
